import { useEffect, useMemo, useRef, useState } from 'react'
import videojs from 'video.js'
// Базовый css video.js — не "скин" (vjs-default-skin мы нигде не подключаем,
// поэтому синяя тема не применится), а обязательная механика: скрывает
// служебный текст для скринридеров ("Play", "Video Player is loading." и т.п.)
// и прячет собственную панель управления video.js, когда controls: false.
// Без него весь этот служебный текст вываливается на экран как есть.
import 'video.js/dist/video-js.css'

const BASE_URL = process.env.REACT_APP_API_URL || ''
const resolveUrl = (src) => (src?.startsWith('http') || src?.startsWith('blob:') ? src : `${BASE_URL}${src}`)

const guessType = (src) => {
    if (/\.m3u8($|\?)/i.test(src)) return 'application/x-mpegURL'
    return 'video/mp4'
}

// VHS (движок HLS в video.js) сам хранит список доступных качеств из
// master-плейлиста — за отдельным API/библиотекой для этого лезть не нужно.
// { IWillNotUseThisInPlugins: true } — это не магическое заклинание, а просто
// обязательный "да, я знаю, что лезу во внутренности напрямую" флаг, которого
// требует сам video.js для доступа к .tech() снаружи его системы плагинов.
function getRepresentations(player) {
    try {
        return player.tech({ IWillNotUseThisInPlugins: true }).vhs?.representations?.() || []
    } catch {
        return []
    }
}

// Спрашиваем сервер "готов ли HLS для этого видео", и сразу решаем, с каким
// источником вообще создавать плеер — video.js не умеет "на лету" незаметно
// переключить mp4 на HLS без видимой перезагрузки, поэтому источник должен
// быть известен ДО первого рендера видео, а не подставляться потом.
async function resolveSource(src) {
    try {
        // credentials: 'include' — обязательно, т.к. клиент и сервер на разных
        // портах (localhost:3000 / localhost:8000): без этого браузер не
        // отправит куку mediaToken, и сервер ответит 401.
        const res = await fetch(`${BASE_URL}/api/video/status?url=${encodeURIComponent(src)}`, {
            credentials: 'include',
        })
        if (res.ok) {
            const data = await res.json()
            if (data.status === 'ready' && data.hlsUrl) {
                // Для чата hlsUrl теперь — наш собственный защищённый путь
                // (/uploads/chat-.../hls/.../output.m3u8), а не готовая ссылка
                // на MinIO — resolveUrl добавит BASE_URL, если нужно (для
                // постов hlsUrl уже абсолютный, resolveUrl его не тронет).
                //
                // withCredentials — video.js сам, напрямую (не через этот fetch),
                // будет запрашивать манифест у нашего сервера. Без этого флага
                // браузер не приложит куку mediaToken к этому запросу (порт
                // клиента и порт API разные — межпортовый запрос), и приватное
                // видео из чата не проиграется. Ставим true только для НАШЕГО
                // собственного защищённого пути, не для чужих (MinIO) ссылок.
                return {
                    src: resolveUrl(data.hlsUrl),
                    type: 'application/x-mpegURL',
                    withCredentials: data.hlsUrl.startsWith('/uploads/'),
                    status: 'ready',
                }
            }
            // pending/processing — обработка ещё идёт, failed — не получилось,
            // none — для этого файла обработки вообще не будет (например, это
            // старое видео до появления пайплайна). Возвращаем status дальше,
            // чтобы вызывающий код знал, стоит ли переспрашивать позже.
            return { src: resolveUrl(src), type: guessType(src), withCredentials: false, status: data.status }
        }
    } catch {
        // сервер недоступен или эндпоинта нет — просто играем оригинал как есть
    }
    return { src: resolveUrl(src), type: guessType(src), withCredentials: false, status: 'unknown' }
}

// Меняет источник плеера на лету, сохраняя место и состояние воспроизведения —
// используется и когда истекла подписанная ссылка, и когда видео "дозрело"
// до HLS уже после того, как плеер открылся с обычным mp4.
function swapSource(player, source) {
    const wasPlaying = !player.paused()
    const savedTime = player.currentTime() || 0
    player.src({ src: source.src, type: source.type })
    player.one('loadedmetadata', () => {
        player.currentTime(savedTime)
        if (wasPlaying) player.play()
    })
}

// "Мотор" плеера: поднимает video.js без штатного скина (controls: false) и отдаёт
// наружу простой API (play/pause/seek/...) плюс срез состояния для отрисовки своих
// контролов. Ничего не знает о том, как контролы выглядят.
export function useVideoPlayer({ src, autoPlay = false, muted = false, loop = false } = {}) {
    const videoRef = useRef(null)
    const playerRef = useRef(null)

    const [state, setState] = useState({
        ready: false,
        playing: false,
        currentTime: 0,
        duration: 0,
        bufferedPercent: 0,
        volume: 1,
        muted,
        rate: 1,
        videoWidth: 0,
        videoHeight: 0,
        isPip: false,
        qualityLevels: [], // доступные качества этого видео (пусто — если это не HLS вовсе)
        quality: 'auto',   // что выбрал человек: 'auto' или конкретная высота (360, 720, ...)
        activeHeight: 0,   // какое качество реально играет сейчас (в т.ч. при 'auto')
    })

    useEffect(() => {
        if (!videoRef.current || !src) return

        // cancelled — на случай, если src сменится (или компонент размонтируется)
        // раньше, чем придёт ответ от resolveSource: тогда просто ничего не делаем
        // с устаревшим результатом, вместо того чтобы создавать лишний плеер.
        let cancelled = false

        let upgradePollId = null

        resolveSource(src).then((source) => {
            if (cancelled || !videoRef.current) return
            initPlayer(source)

            // Видео ещё обрабатывается (pending/processing) — вместо того чтобы
            // навсегда остаться на обычном mp4, тихо спрашиваем сервер каждые
            // несколько секунд. Как только станет ready — сами переключим
            // источник, без перезагрузки страницы. failed/none/ready — спрашивать
            // больше не о чём, ничего не изменится.
            if (source.status === 'pending' || source.status === 'processing') {
                upgradePollId = setInterval(async () => {
                    const fresh = await resolveSource(src)
                    if (fresh.status === 'ready') {
                        clearInterval(upgradePollId)
                        const player = playerRef.current
                        if (cancelled || !player) return

                        // Не прерываем активный просмотр — если сейчас играет,
                        // подменим источник тихо, только когда сам поставят на
                        // паузу (или видео закончится). Если уже на паузе —
                        // менять нечего прерывать, переключаем сразу.
                        if (player.paused()) {
                            swapSource(player, fresh)
                        } else {
                            player.one('pause', () => swapSource(player, fresh))
                        }
                    } else if (fresh.status !== 'pending' && fresh.status !== 'processing') {
                        clearInterval(upgradePollId)
                    }
                }, 5000)
            }
        })

        function initPlayer(source) {
            const player = videojs(videoRef.current, {
                controls: false,
                controlBar: false, // свою панель рисуем сами — не даём video.js вообще создавать его собственную
                bigPlayButton: false, // свой центральный кружок рисуем сами в VideoPlayer.jsx
                // 'any' вместо true — важно теперь, когда звук по умолчанию
                // включён: браузер запрещает автозапуск со звуком, и обычный
                // autoplay: true просто оставил бы видео стоять на паузе.
                // 'any' — штатный фолбэк video.js: пробует со звуком, а если
                // браузер отказал — приглушает и всё-таки запускает.
                autoplay: autoPlay ? 'any' : false,
                muted,
                loop,
                preload: 'metadata',
                fluid: false,
                playsinline: true,
                // videojs-http-streaming (VHS) — плагин для проигрывания HLS —
                // уже встроен в video.js "из коробки" с версии 7, отдельно
                // ставить не нужно: он сам включается, если видит source с
                // type: 'application/x-mpegURL' (см. resolveSource выше).
                sources: [{ src: source.src, type: source.type }],
                html5: { vhs: { withCredentials: source.withCredentials || false } },
            })
            playerRef.current = player
            attachPlayerEvents(player)
        }

        function attachPlayerEvents(player) {
            const syncState = () => {
                setState((prev) => ({
                    ...prev,
                    playing: !player.paused(),
                    currentTime: player.currentTime() || 0,
                    duration: player.duration() || 0,
                    bufferedPercent: player.bufferedPercent() || 0,
                    volume: player.volume(),
                    muted: player.muted(),
                    rate: player.playbackRate(),
                }))
            }

            const syncDimensions = () => {
                setState((prev) => ({
                    ...prev,
                    videoWidth: player.videoWidth() || 0,
                    videoHeight: player.videoHeight() || 0,
                }))
            }

            // К моменту loadedmetadata VHS уже разобрал master-плейлист и
            // выбрал стартовое качество — представления уже доступны.
            const syncQualityLevels = () => {
                const reps = getRepresentations(player)
                if (!reps.length) return
                const heights = [...new Set(reps.map((r) => r.height))].sort((a, b) => b - a)
                setState((prev) => ({ ...prev, qualityLevels: heights }))
            }

            // Какое качество реально играет ПРЯМО СЕЙЧАС — обновляется и при
            // ручном выборе, и когда VHS сам переключается в режиме 'auto'
            // из-за смены скорости интернета.
            const syncActiveHeight = () => {
                const height = player.tech({ IWillNotUseThisInPlugins: true }).vhs?.playlists?.media?.()?.attributes?.RESOLUTION?.height
                if (height) setState((prev) => ({ ...prev, activeHeight: height }))
            }

            player.on('loadedmetadata', () => {
                syncDimensions()
                syncState()
                syncQualityLevels()
                syncActiveHeight()
                player.tech({ IWillNotUseThisInPlugins: true }).vhs?.playlists?.on('mediachange', syncActiveHeight)
            })
            player.on('timeupdate', syncState)
            player.on('play', syncState)
            player.on('pause', syncState)
            player.on('volumechange', syncState)
            player.on('ratechange', syncState)
            player.on('progress', syncState)

            // Подписанные ссылки на HLS-сегменты живут 1 час (см. getSignedHlsManifest
            // на сервере). Если человек смотрит видео дольше или досмотрел с паузой
            // спустя час — сегменты начнут отдавать 403, и video.js покажет ошибку.
            // Вместо этого тихо просим у сервера свежий манифест (это снова пройдёт
            // проверку прав) и продолжаем с того же места, без перезагрузки страницы.
            let refreshedOnError = false
            player.on('error', async () => {
                if (refreshedOnError) return // не пытаемся бесконечно, если дело не в истёкшей ссылке
                refreshedOnError = true

                const fresh = await resolveSource(src)
                swapSource(player, fresh)
            })

            player.ready(() => {
                setState((prev) => ({ ...prev, ready: true }))

                const videoEl = player.el().querySelector('video')
                videoEl?.addEventListener('enterpictureinpicture', () => {
                    setState((prev) => ({ ...prev, isPip: true }))
                })
                videoEl?.addEventListener('leavepictureinpicture', () => {
                    setState((prev) => ({ ...prev, isPip: false }))
                })
            })
        }

        return () => {
            cancelled = true
            if (upgradePollId) clearInterval(upgradePollId)
            playerRef.current?.dispose()
            playerRef.current = null
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src])

    const api = useMemo(() => ({
        play: () => playerRef.current?.play(),
        pause: () => playerRef.current?.pause(),
        toggle: () => {
            const p = playerRef.current
            if (!p) return
            p.paused() ? p.play() : p.pause()
        },
        seek: (time) => {
            const p = playerRef.current
            if (!p) return
            p.currentTime(Math.max(0, Math.min(p.duration() || 0, time)))
        },
        seekBy: (delta) => {
            const p = playerRef.current
            if (!p) return
            p.currentTime(Math.max(0, Math.min(p.duration() || 0, p.currentTime() + delta)))
        },
        setVolume: (v) => playerRef.current?.volume(Math.max(0, Math.min(1, v))),
        toggleMute: () => {
            const p = playerRef.current
            if (!p) return
            p.muted(!p.muted())
        },
        setRate: (rate) => playerRef.current?.playbackRate(rate),
        // height === 'auto' — включаем обратно все представления, VHS сам
        // решает, что играть, по скорости интернета (как было по умолчанию).
        // height === число — включаем только его, выключив остальные: VHS
        // ничего другого выбрать уже не может.
        setQuality: (height) => {
            const player = playerRef.current
            if (!player) return
            const reps = getRepresentations(player)
            reps.forEach((r) => r.enabled(height === 'auto' || r.height === height))
            setState((prev) => ({ ...prev, quality: height }))
        },
        requestPip: () => {
            const videoEl = playerRef.current?.el().querySelector('video')
            videoEl?.requestPictureInPicture?.()
        },
    }), [])

    return { videoRef, state, ...api }
}
